import { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import type {
  PSDContext,
  PSDGeneral,
  PSDSieveDetail,
  PSDHydroDetail,
} from '../../../types/psd';
import {
  buildCurveLive,
  computeDSizesLive,
  computeFractionsLive,
  computeSieveRowsLive,
  computeHydroRowLive,
} from './computations';

interface Props {
  context: PSDContext;
  general: PSDGeneral;
  sieveDetails: PSDSieveDetail[];
  hydroDetails: PSDHydroDetail[];
}

const fmt = (v: number | null | undefined, d = 1): string =>
  v === null || v === undefined || Number.isNaN(v) ? '—' : v.toFixed(d);
const fmtInt = (v: number | null | undefined): string =>
  v === null || v === undefined || Number.isNaN(v) ? '—' : Math.round(v).toString();
const fmt3 = (v: number | null | undefined): string =>
  v === null || v === undefined || Number.isNaN(v) ? '—' : v.toFixed(3);

export const PSDSummaryTable = ({ context, general, sieveDetails, hydroDetails }: Props) => {
  const theme = useTheme();

  const curve = useMemo(
    () => buildCurveLive(sieveDetails, hydroDetails, general),
    [sieveDetails, hydroDetails, general],
  );
  const dsizes = useMemo(() => computeDSizesLive(curve), [curve]);
  const fractions = useMemo(() => computeFractionsLive(curve), [curve]);

  // Build the right-side Results table — sieves first (sorted descending), then hydros
  const sieveComputed = useMemo(
    () => computeSieveRowsLive(sieveDetails, general),
    [sieveDetails, general],
  );
  const hydroComputed = useMemo(
    () => hydroDetails.map((h) => computeHydroRowLive(h, general)),
    [hydroDetails, general],
  );

  const resultRows = useMemo(() => {
    const rows: { sizeMm: number; passingPct: number | null }[] = [];
    sieveDetails.forEach((s, i) => {
      rows.push({ sizeMm: s.sieveSizeMm, passingPct: sieveComputed[i].passingWholePct });
    });
    hydroDetails.forEach((_, i) => {
      const c = hydroComputed[i];
      if (c.particleDiameterMm !== null) {
        rows.push({ sizeMm: c.particleDiameterMm, passingPct: c.percentPassingWhole });
      }
    });
    return rows.sort((a, b) => b.sizeMm - a.sizeMm);
  }, [sieveDetails, hydroDetails, sieveComputed, hydroComputed]);

  const headerSx = {
    fontSize: '0.78rem', fontWeight: 700, color: 'primary.main', letterSpacing: 0.4,
    textTransform: 'uppercase' as const,
  };

  const cardSx = {
    p: 1.5, borderRadius: 2,
    border: `1px solid ${alpha(theme.border?.color ?? '#ccc', 0.35)}`,
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
  };

  const finalUSCS = general.overrideUSCSDescription ?? general.uscsDescription ?? '—';

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
      {/* Project + Sample info */}
      <Paper elevation={0} sx={cardSx}>
        <Typography sx={headerSx}>Project Information</Typography>
        <Box sx={{ mt: 0.75 }}>
          <InfoRow label="Project No." value={context.projectId || '—'} />
          <InfoRow label="Project Name" value={context.projectName || '—'} />
          <InfoRow label="Project Location" value={context.siteName || '—'} />
          <InfoRow label="Client" value={context.clientName || '—'} />
        </Box>
      </Paper>

      <Paper elevation={0} sx={cardSx}>
        <Typography sx={headerSx}>Sample Information</Typography>
        <Box sx={{ mt: 0.75 }}>
          <InfoRow label="Borehole / Test Pit" value={context.locationName || '—'} />
          <InfoRow label="Sample ID" value={context.specimenRef
            ? `${context.sampleReference} (${context.specimenRef})`
            : (context.sampleReference || '—')} />
          <InfoRow label="Sample Depth" value={context.specimenDepth != null
            ? `${context.specimenDepth} m`
            : (context.depth ? `${context.depth} m` : '—')} />
          <InfoRow label="Sample Type" value={context.sampleType || '—'} />
        </Box>
      </Paper>

      {/* Gradation Summary */}
      <Paper elevation={0} sx={cardSx}>
        <Typography sx={headerSx}>Gradation Summary</Typography>
        <Box sx={{
          mt: 0.75,
          display: 'grid',
          gridTemplateColumns: '1.4fr 80px',
          rowGap: 0.4,
          alignItems: 'center',
          fontSize: '0.78rem',
        }}>
          <SummaryRow label="Coarser than Gravel (%)" value={fmtInt(fractions.coarserThanGravelPct)} />
          <SummaryRow label="Gravel (%)" value={fmtInt(fractions.gravelPct)} />
          <SummaryRow label="Sand (%)" value={fmtInt(fractions.sandPct)} />
          <SummaryRow label="Fines [Silt / Clay] (%)" value={
            fractions.finesPct !== null
              ? `${fmtInt(fractions.finesPct)} [${fmtInt(fractions.siltPct)} / ${fmtInt(fractions.clayPct)}]`
              : '—'
          } />
          <SummaryRow label="D₆₀ (mm)" value={fmt3(dsizes.D60)} />
          <SummaryRow label="D₃₀ (mm)" value={fmt3(dsizes.D30)} />
          <SummaryRow label="D₁₀ (mm)" value={fmt3(dsizes.D10)} />
          <SummaryRow label="C_c" value={fmt(dsizes.Cc, 2)} />
          <SummaryRow label="C_u" value={fmt(dsizes.Cu, 2)} />
        </Box>
      </Paper>

      {/* Results table — Size | Passing % */}
      <Paper elevation={0} sx={cardSx}>
        <Typography sx={headerSx}>Results</Typography>
        <Box sx={{ mt: 0.75, maxHeight: 280, overflowY: 'auto' }}>
          <Box sx={{
            display: 'grid', gridTemplateColumns: '1fr 1fr',
            position: 'sticky', top: 0, backgroundColor: theme.palette.background.paper, zIndex: 1,
            py: 0.4, borderBottom: '1px solid', borderColor: 'divider',
          }}>
            <Box sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', pl: 1 }}>Size (mm)</Box>
            <Box sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textAlign: 'right', pr: 1 }}>Passing (%)</Box>
          </Box>
          {resultRows.map((r, i) => (
            <Box key={i} sx={{
              display: 'grid', gridTemplateColumns: '1fr 1fr',
              py: 0.3, borderBottom: '1px solid', borderColor: alpha(theme.palette.divider, 0.3),
            }}>
              <Box sx={{ fontSize: '0.74rem', pl: 1 }}>{r.sizeMm}</Box>
              <Box sx={{ fontSize: '0.74rem', textAlign: 'right', pr: 1, fontVariantNumeric: 'tabular-nums' }}>
                {fmt(r.passingPct, 1)}
              </Box>
            </Box>
          ))}
          {resultRows.length === 0 && (
            <Box sx={{ p: 2, textAlign: 'center', fontSize: '0.78rem', color: 'text.disabled' }}>
              No data yet — fill the sieve table or hydrometer readings on the Result Entry tab.
            </Box>
          )}
        </Box>
      </Paper>

      {/* USCS row spanning full width */}
      <Paper elevation={0} sx={{ ...cardSx, gridColumn: '1 / -1' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, alignItems: 'center' }}>
          <Box>
            <Typography sx={headerSx}>Preparation Method</Typography>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, mt: 0.5 }}>
              {general.preparation === 'OvenDry' ? `Oven dried at ${general.ovenTemperatureC ?? '—'}°C`
                : general.preparation === 'AirDry' ? 'Air dried'
                : general.preparation === 'AsReceived' ? 'As received'
                : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography sx={headerSx}>USCS Description (ASTM D2487)</Typography>
            <Typography sx={{
              fontSize: '1.4rem', fontWeight: 700, mt: 0.25,
              color: finalUSCS === '—' ? 'text.disabled' : 'primary.main',
            }}>
              {finalUSCS}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

const InfoRow = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{
    display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'baseline',
    py: 0.3, borderBottom: '1px solid', borderColor: 'divider',
  }}>
    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, textAlign: 'right',
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {value}
    </Typography>
  </Box>
);

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <>
    <Box sx={{ pl: 1, fontSize: '0.78rem', borderBottom: '1px solid', borderColor: 'divider', py: 0.4 }}>{label}</Box>
    <Box sx={{
      pr: 1, fontSize: '0.85rem', fontWeight: 700, textAlign: 'right',
      borderBottom: '1px solid', borderColor: 'divider', py: 0.4,
      fontVariantNumeric: 'tabular-nums',
    }}>{value}</Box>
  </>
);
